import { useCallback, useState } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema.ts";
import type {
	PaymentNonce,
	SavedAch,
	SavedCard,
} from "~/shared/types/payment-types.ts";

import {
	PAYMENT_CONSTRAINTS,
	PAYMENT_MODAL_TITLES,
} from "../utils/constants.ts";
import { useGridColumns } from "./use-grid-columns.tsx";
import { usePaymentAmounts } from "./use-payment-amounts.ts";

type UsePaymentModalProps = {
	onClose: () => void;
	title: string;
	select: Invoice[];
	onRemoveInvoice?: (invoiceId: string) => void;
	onExecutePayment?: (data: {
		amounts: number;
		invoiceAmounts: Record<string, number>;
		description: string;
		isLumpSum: boolean;
	}) => void | Promise<void>;
	handleApplyPaymentMethod: (
		paymentMethod: SavedCard | SavedAch | PaymentNonce | null,
	) => void;
};

export function usePaymentModal({
	onClose,
	title,
	select,
	onRemoveInvoice,
	onExecutePayment,
	handleApplyPaymentMethod,
}: UsePaymentModalProps) {
	const [description, setDescription] = useState("");
	const [amounts, setAmounts] = useState(0);
	const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);

	const {
		amounts: invoiceAmounts,
		totalPayment,
		updateAmount,
		resetAmounts: resetInvoiceAmounts,
	} = usePaymentAmounts(select);

	const isLumpSumPayment = title === PAYMENT_MODAL_TITLES.PAY_LUMP_SUM;

	const handleDeleteInvoice = useCallback(
		(invoiceId: string) => {
			onRemoveInvoice?.(invoiceId);
		},
		[onRemoveInvoice],
	);

	const resetAllAmounts = useCallback(() => {
		setAmounts(0);
		resetInvoiceAmounts();
	}, [resetInvoiceAmounts]);

	const columns = useGridColumns({
		onAmountChange: (id: string, amount: number) => {
			updateAmount(id, amount);
		},
		onDeleteInvoice: handleDeleteInvoice,
	});

	// Raw callbacks for non-grid consumers (e.g. mobile invoice cards)
	const onAmountChange: (invoiceId: string, amount: number) => void =
		updateAmount;
	const onDeleteInvoice: (invoiceId: string) => void = handleDeleteInvoice;

	const handleClose = useCallback(() => {
		onClose();
		setDescription("");
		resetAllAmounts();
		handleApplyPaymentMethod(null);
	}, [onClose, resetAllAmounts, handleApplyPaymentMethod]);

	// Use totalPayment from usePaymentAmounts for non-lump-sum payments
	const effectiveAmount = isLumpSumPayment ? amounts : totalPayment;

	const handleExecutePaymentAction = useCallback(() => {
		onExecutePayment?.({
			amounts: effectiveAmount,
			invoiceAmounts,
			description,
			isLumpSum: isLumpSumPayment,
		});
	}, [
		onExecutePayment,
		effectiveAmount,
		invoiceAmounts,
		description,
		isLumpSumPayment,
	]);

	const handleDescriptionChange = useCallback((value: string) => {
		if (value.length <= PAYMENT_CONSTRAINTS.DESCRIPTION_MAX_LENGTH) {
			setDescription(value);
		}
	}, []);

	const openPaymentMethodModal = useCallback(() => {
		setShowPaymentMethodModal(true);
	}, []);

	const closePaymentMethodModal = useCallback(() => {
		setShowPaymentMethodModal(false);
	}, []);

	return {
		description,
		amounts,
		showPaymentMethodModal,
		isLumpSumPayment,
		columns,
		effectiveAmount,
		calculatedTotal: totalPayment,
		handleClose,
		handleExecutePaymentAction,
		handleDescriptionChange,
		openPaymentMethodModal,
		closePaymentMethodModal,
		setAmounts,
		onAmountChange,
		onDeleteInvoice,
	};
}
